'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, CheckCircle2, Info } from 'lucide-react'

export type ToastKind = 'info' | 'success' | 'error' | 'warning'

type ToastEntry = {
  id: number
  kind: ToastKind
  content: string
}

/**
 * The hook & provider accept either
 *   showToast('ok')
 *   showToast('oops', 'error')           // reference jsx style
 *   showToast('slow', 4000)              // antd-style duration ms
 *   showToast('done', 'success', 2000)   // both at once
 * Keeping all three signatures lets existing call sites keep working while
 * new code can follow the reference mock.
 */
type ShowToast = (content: string, kindOrDuration?: ToastKind | number, durationMs?: number) => void

type ToastContextValue = {
  showToast: ShowToast
  info: (content: string, durationMs?: number) => void
  success: (content: string, durationMs?: number) => void
  warning: (content: string, durationMs?: number) => void
  error: (content: string, durationMs?: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const MIN_DURATION_MS = 1000
const DEFAULT_DURATION_MS = 3000
const MAX_STACKED_TOASTS = 3

/**
 * Module-level imperative channel. Populated by the first `<ToastProvider>`
 * that mounts so non-React code (e.g. `lib/api.ts`) can surface server-side
 * errors without a hook. Before the provider mounts, calls fall back to
 * `console.error` / `console.info` so we never silently drop messages.
 */
let dispatcher: ((kind: ToastKind, content: string, durationMs?: number) => void) | null = null

function dispatch(kind: ToastKind, content: string, durationMs?: number): void {
  if (!content) return
  if (dispatcher) {
    dispatcher(kind, content, durationMs)
    return
  }
  // Provider not mounted yet (e.g. called during module init). Echo to
  // console so the event is not lost entirely.
  const logger = kind === 'error' ? console.error : console.info
  logger(`[toast:${kind}] ${content}`)
}

/**
 * antd-style imperative `message` API. Usable from any module, not just React
 * components. Each call enqueues a toast on the active `<ToastProvider>`.
 *
 * Usage:
 *   import {message} from '@/components/Toast'
 *   message.error('请求失败：' + reason)
 */
export const message = {
  info: (content: string, durationMs?: number) => dispatch('info', content, durationMs),
  success: (content: string, durationMs?: number) => dispatch('success', content, durationMs),
  warning: (content: string, durationMs?: number) => dispatch('warning', content, durationMs),
  error: (content: string, durationMs?: number) => dispatch('error', content, durationMs),
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([])
  const [mounted, setMounted] = useState(false)
  const nextIdRef = useRef(1)
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    setMounted(true)
    const timers = timersRef.current
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  const push = useCallback((kind: ToastKind, content: string, durationMs?: number) => {
    if (!content) return
    const effective = Math.max(durationMs ?? DEFAULT_DURATION_MS, MIN_DURATION_MS)
    const id = nextIdRef.current++
    setEntries((prev) => {
      const next = [...prev, { id, kind, content }]
      // Drop oldest if we exceed the stack cap. Also clear its timer
      // so stale closures don't try to dismiss an entry that's gone.
      while (next.length > MAX_STACKED_TOASTS) {
        const dropped = next.shift()
        if (dropped) {
          const timer = timersRef.current.get(dropped.id)
          if (timer) {
            clearTimeout(timer)
            timersRef.current.delete(dropped.id)
          }
        }
      }
      return next
    })
    const timer = setTimeout(() => {
      setEntries((prev) => prev.filter((entry) => entry.id !== id))
      timersRef.current.delete(id)
    }, effective)
    timersRef.current.set(id, timer)
  }, [])

  // Wire the module-level dispatcher the first time the provider mounts.
  // If multiple providers mount, the latest one wins (unmounting the
  // provider clears it).
  useEffect(() => {
    dispatcher = push
    return () => {
      if (dispatcher === push) dispatcher = null
    }
  }, [push])

  const showToast = useCallback<ShowToast>(
    (content, kindOrDuration, durationMs) => {
      const kind: ToastKind = typeof kindOrDuration === 'string' ? kindOrDuration : 'info'
      const duration = typeof kindOrDuration === 'number' ? kindOrDuration : durationMs
      push(kind, content, duration)
    },
    [push],
  )

  const value: ToastContextValue = {
    showToast,
    info: (content, durationMs) => push('info', content, durationMs),
    success: (content, durationMs) => push('success', content, durationMs),
    warning: (content, durationMs) => push('warning', content, durationMs),
    error: (content, durationMs) => push('error', content, durationMs),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {mounted && entries.length > 0
        ? createPortal(
            <div className="fixed top-6 inset-x-0 z-overlay flex flex-col items-center gap-3 pointer-events-none">
              {entries.map((entry) => (
                <ToastCard key={entry.id} entry={entry} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  )
}

function ToastCard({ entry }: { entry: ToastEntry }) {
  const { kind, content } = entry
  const iconColor = iconColorFor(kind)
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      aria-live={kind === 'error' ? 'assertive' : 'polite'}
      className="min-w-40 max-w-[min(360px,calc(100vw-32px))] bg-white px-5 py-3 rounded-lg shadow-toast border border-gray-100 inline-flex items-center justify-center gap-3"
    >
      <ToastIcon kind={kind} className={`${iconColor} shrink-0`} />
      <span className="min-w-0 text-gray-700 text-sm font-medium tracking-wide text-center break-words">{content}</span>
    </div>
  )
}

function ToastIcon({ kind, className }: { kind: ToastKind; className: string }) {
  if (kind === 'success') return <CheckCircle2 size={18} className={className} />
  if (kind === 'error' || kind === 'warning') return <AlertCircle size={18} className={className} />
  return <Info size={18} className={className} />
}

function iconColorFor(kind: ToastKind): string {
  if (kind === 'success') return 'text-accent'
  if (kind === 'error') return 'text-red-500'
  if (kind === 'warning') return 'text-orange-500'
  return 'text-blue-500'
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>')
  }
  return ctx
}
